import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Recycle, Shield, Users, Rocket, Zap, Target, ChevronDown, Star } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

// Focus Area icons mapping
const FOCUS_AREA_ICONS = {
  'A1': Recycle,
  'A2': Shield,
  'A3': Users,
  'A4': Rocket,
};

const FOCUS_AREA_COLORS = {
  'A1': '#8DC21F',
  'A2': '#3b82f6',
  'A3': '#f59e0b',
  'A4': '#8b5cf6',
};

function ChallengeCard({ challenge, index, inView, onSubmit, lang }) {
  const [expanded, setExpanded] = useState(false);
  const isTop3 = challenge.priority === 'TOP3';
  const Icon = FOCUS_AREA_ICONS[challenge.focusArea] || Target;
  const areaColor = FOCUS_AREA_COLORS[challenge.focusArea] || '#8DC21F';

  return (
    <motion.div
      data-testid="challenge-card"
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="challenge-card group"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px',
        overflow: 'hidden',
        backdropFilter: 'blur(12px)',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Top Priority Badge */}
      {isTop3 && (
        <div 
          className="absolute top-3 right-3 z-10 flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider"
          style={{
            background: 'linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,165,0,0.15))',
            border: '1px solid rgba(255,215,0,0.3)',
            color: '#ffd700',
            boxShadow: '0 0 12px rgba(255,215,0,0.2)',
          }}
        >
          <Star size={10} fill="#ffd700" />
          TOP 3
        </div>
      )}

      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-5 md:p-6 relative"
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div 
            className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: `${areaColor}15`,
              border: `1px solid ${areaColor}30`,
            }}
          >
            <Icon size={18} style={{ color: areaColor }} />
          </div>

          <div className="flex-1 min-w-0">
            {/* Badges */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span
                className="text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-full whitespace-nowrap"
                style={{
                  background: `${areaColor}15`,
                  color: areaColor,
                  border: `1px solid ${areaColor}30`,
                }}
              >
                {challenge.focusAreaName}
              </span>
              {challenge.priority === 'TOP5' && (
                <span
                  className="text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-full whitespace-nowrap"
                  style={{
                    background: 'rgba(59,130,246,0.1)',
                    color: '#3b82f6',
                    border: '1px solid rgba(59,130,246,0.25)',
                  }}
                >
                  {lang === 'es' ? 'Radar Activo' : 'Active Radar'}
                </span>
              )}
              {challenge.year && (
                <span
                  className="text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.4)' }}
                >
                  {challenge.year}
                </span>
              )}
            </div>

            {/* Title */}
            <h3 className="text-base font-semibold text-white/90 leading-snug mb-2 pr-8">
              {challenge.title}
            </h3>

            {/* Impact badge */}
            <div className="flex items-center gap-2">
              <Zap size={12} style={{ color: areaColor }} />
              <span className="text-xs text-white/50">{challenge.impact}</span>
            </div>
          </div>

          <ChevronDown
            size={18}
            className="text-white/40 flex-shrink-0 mt-1 transition-transform duration-300"
            style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
          />
        </div>
      </button>

      {/* Expandable detail */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            <div 
              className="px-5 md:px-6 pb-5 md:pb-6 pt-3" 
              style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
            >
              <p className="text-sm text-white/60 leading-relaxed mb-4">
                {challenge.description}
              </p>

              {challenge.objectives && (
                <div className="mb-4">
                  <h4 className="text-xs font-bold text-white/70 uppercase tracking-wider mb-2">
                    {lang === 'es' ? 'Objetivo' : 'Objective'}
                  </h4>
                  <p className="text-xs text-white/55 leading-relaxed">
                    {challenge.objectives}
                  </p>
                </div>
              )}

              <button
                data-testid="challenge-submit-btn"
                onClick={onSubmit}
                className="flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl transition-all"
                style={{ 
                  background: areaColor, 
                  color: '#000', 
                  border: 'none', 
                  boxShadow: `0 6px 20px ${areaColor}40`, 
                  cursor: 'pointer' 
                }}
              >
                {lang === 'es' ? 'Proponer Solución' : 'Submit Solution'}
                <ArrowRight size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function ChallengesSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { t, lang } = useLanguage();

  // TOP 8 Innovation Challenges 2026 - Real data from committee
  const challenges2026 = [
    // TOP 3 - Main Focus
    {
      id: 'R1',
      priority: 'TOP3',
      focusArea: 'A1',
      focusAreaName: lang === 'es' ? 'Á1: NUEVAS CADENAS DE VALOR' : 'A1: NEW VALUE CHAINS',
      year: 'H2 2026',
      title: lang === 'es' 
        ? 'Outputs de planta sin mercado' 
        : 'Plant Outputs Without Market',
      description: lang === 'es'
        ? 'Transformar residuos de planta (bioestabilizado, materiales difíciles como film, PP, PET, madera) en productos comercializables. Generar nuevas fuentes de ingresos a partir de lo que actualmente es un coste de gestión o termina en vertedero.'
        : 'Transform plant waste outputs (biostabilized, difficult materials like film, PP, PET, wood) into marketable products. Generate new revenue streams from what is currently a management cost or ends up in landfills.',
      impact: lang === 'es' ? 'Convertir costes en ingresos' : 'Turn costs into revenue',
      objectives: lang === 'es'
        ? 'Crear productos comerciales derivados de residuos. Aumentar ingresos. Reducir costes operativos y huella ambiental.'
        : 'Create commercial products from waste. Increase revenue. Reduce operational costs and environmental footprint.',
    },
    {
      id: 'R2',
      priority: 'TOP3',
      focusArea: 'A2',
      focusAreaName: lang === 'es' ? 'Á2: TECNOLOGÍA Y SEGURIDAD' : 'A2: TECH & SAFETY',
      year: 'H2 2026',
      title: lang === 'es'
        ? 'Reducción de riesgos físicos críticos'
        : 'Critical Physical Risk Reduction',
      description: lang === 'es'
        ? 'Identificar y mitigar riesgos físicos críticos para el personal: pinchazos y cortes en plantas, caídas de carga suspendida, detección de gases y espacios confinados, seguridad vial (cinturón, distracción, velocidad, vuelcos).'
        : 'Identify and mitigate critical physical risks for personnel: punctures and cuts in plants, falling suspended loads, gas detection and confined spaces, road safety (belt, distraction, speed, rollovers).',
      impact: lang === 'es' ? 'Seguridad personal + diferenciador licitaciones' : 'Personnel safety + tender differentiator',
      objectives: lang === 'es'
        ? 'Reducir significativamente accidentes laborales. Mejorar bienestar de empleados. Fortalecer reputación como empleador seguro.'
        : 'Significantly reduce workplace accidents. Improve employee well-being. Strengthen reputation as a safe employer.',
    },
    {
      id: 'R3',
      priority: 'TOP3',
      focusArea: 'A2',
      focusAreaName: lang === 'es' ? 'Á2: TECNOLOGÍA Y SEGURIDAD' : 'A2: TECH & SAFETY',
      year: 'H2 2026',
      title: lang === 'es'
        ? 'Sensorización y gestor común de flota'
        : 'Fleet Sensorization & Common Manager',
      description: lang === 'es'
        ? 'Implementar solución integral de sensorización y gestión para toda la flota propia de Valoriza, incluyendo maquinaria heredada. Tener una solución propietaria o partner exclusivo para toda la flota.'
        : 'Implement comprehensive sensorization and management solution for Valoriza\'s entire fleet, including inherited machinery. Have a proprietary or exclusive partner solution for the entire fleet.',
      impact: lang === 'es' ? 'Eficiencia operativa transversal' : 'Cross-cutting operational efficiency',
      objectives: lang === 'es'
        ? 'Optimizar eficiencia de flota. Mejorar mantenimiento predictivo. Reducir costes operativos (combustible, mantenimiento). Mejorar toma de decisiones con datos.'
        : 'Optimize fleet efficiency. Improve predictive maintenance. Reduce operational costs (fuel, maintenance). Enhance data-driven decision-making.',
    },
    // TOP 5 - Active Radar
    {
      id: 'R4',
      priority: 'TOP5',
      focusArea: 'A1',
      focusAreaName: lang === 'es' ? 'Á1: NUEVAS CADENAS DE VALOR' : 'A1: NEW VALUE CHAINS',
      year: '2026-2027',
      title: lang === 'es'
        ? 'Nuevos flujos emergentes'
        : 'New Emerging Waste Streams',
      description: lang === 'es'
        ? 'Posicionar a Valoriza para gestionar y procesar nuevos flujos de residuos emergentes: baterías, paneles fotovoltaicos (PVs), palas de aerogenerador, residuos de aparatos eléctricos y electrónicos (RAEEs).'
        : 'Position Valoriza to handle and process new emerging waste streams: batteries, photovoltaic panels (PVs), wind turbine blades, waste electrical and electronic equipment (WEEE).',
      impact: lang === 'es' ? 'Nuevas líneas de negocio futuras' : 'New future business lines',
      objectives: lang === 'es'
        ? 'Desarrollar capacidades para nuevos residuos. Capturar oportunidades de mercado futuras. Contribuir a economía circular de nuevos productos.'
        : 'Develop capabilities for new waste types. Capture future market opportunities. Contribute to circular economy of new products.',
    },
    {
      id: 'R5',
      priority: 'TOP5',
      focusArea: 'A3',
      focusAreaName: lang === 'es' ? 'Á3: CULTURA Y PERSONAS' : 'A3: CULTURE & PEOPLE',
      year: 'Arrastrado 2025',
      title: lang === 'es'
        ? 'Brecha digital en operativos'
        : 'Digital Gap in Operations',
      description: lang === 'es'
        ? 'Reducir la brecha digital y aumentar la adopción de herramientas digitales entre trabajadores operativos. Con +12.000 empleados y niveles variados de formación básica, la falta de cultura digital es la principal barrera para adoptar proyectos de innovación.'
        : 'Reduce digital divide and increase adoption of digital tools among operational workers. With +12,000 employees and varying levels of basic training, lack of digital culture is the main barrier to adopting innovation projects.',
      impact: lang === 'es' ? '+12.000 empleados a capacitar' : '+12,000 employees to upskill',
      objectives: lang === 'es'
        ? 'Mejorar alfabetización digital. Aumentar adopción de herramientas tecnológicas. Mejorar eficiencia operativa mediante integración digital.'
        : 'Improve digital literacy. Increase adoption of technological tools. Enhance operational efficiency through digital integration.',
    },
    {
      id: 'R6',
      priority: 'TOP5',
      focusArea: 'A1',
      focusAreaName: lang === 'es' ? 'Á1: NUEVAS CADENAS DE VALOR' : 'A1: NEW VALUE CHAINS',
      year: 'Arrastrado 2025',
      title: lang === 'es'
        ? 'Captura y almacenamiento de CO₂'
        : 'CO₂ Capture & Storage',
      description: lang === 'es'
        ? 'Desarrollar soluciones para captura y almacenamiento de emisiones de CO₂. Reto estratégico considerado condición para ejecución de cadenas de valor energéticas.'
        : 'Develop solutions for capturing and storing CO₂ emissions. Strategic challenge considered a condition for executing energy value chains.',
      impact: lang === 'es' ? 'Condición para cadenas energéticas' : 'Condition for energy chains',
      objectives: lang === 'es'
        ? 'Reducir emisiones de GEI. Potencial para nuevas fuentes de ingresos (venta de CO₂ capturado). Mejorar perfil de sostenibilidad.'
        : 'Reduce GHG emissions. Potential for new revenue streams (selling captured CO₂). Enhance sustainability profile.',
    },
    {
      id: 'R7',
      priority: 'TOP5',
      focusArea: 'A4',
      focusAreaName: lang === 'es' ? 'Á4: ROBUSTEZ INNOVACIÓN' : 'A4: INNOVATION ROBUSTNESS',
      year: '2026-2027',
      title: lang === 'es'
        ? 'Inteligencia de mercado sistemática'
        : 'Systematic Market Intelligence',
      description: lang === 'es'
        ? 'Evolucionar la plataforma actual de soluciones e incorporaciones del ecosistema hacia una capacidad proactiva de inteligencia tecnológica y competitiva. Anticipar tendencias del sector y necesidades del cliente antes que la competencia.'
        : 'Evolve current platform of solutions and ecosystem incorporations into proactive technological and competitive intelligence capability. Anticipate sector trends and customer needs before competitors.',
      impact: lang === 'es' ? 'Anticipar al cliente y ganar propuestas' : 'Anticipate client & win proposals',
      objectives: lang === 'es'
        ? 'Identificar proactivamente tendencias de mercado. Anticipar estrategias de competidores. Mejorar competitividad de propuestas.'
        : 'Proactively identify market trends. Anticipate competitor strategies. Enhance proposal competitiveness.',
    },
    {
      id: 'R8',
      priority: 'TOP5',
      focusArea: 'A1',
      focusAreaName: lang === 'es' ? 'Á1: NUEVAS CADENAS DE VALOR' : 'A1: NEW VALUE CHAINS',
      year: '2026-2027',
      title: lang === 'es'
        ? 'Batalla social — biometano y WTE'
        : 'Social Battle — Biomethane & WTE',
      description: lang === 'es'
        ? 'Abordar la oposición social y política como principal barrera para despliegue de proyectos de biometano y Waste-to-Energy. Innovar en modelos de comunicación proactiva, plataformas de engagement ciudadano y aprendizaje de experiencias europeas.'
        : 'Address social and political opposition as main barrier to biomethane and Waste-to-Energy project deployment. Innovate in proactive communication models, citizen engagement platforms and learning from European experiences.',
      impact: lang === 'es' ? 'Desbloquear proyectos energéticos' : 'Unlock energy projects',
      objectives: lang === 'es'
        ? 'Superar barreras sociales y políticas. Fomentar aceptación pública de soluciones energéticas sostenibles. Desarrollar estrategias efectivas de comunicación y engagement.'
        : 'Overcome social and political barriers. Foster public acceptance of sustainable energy solutions. Develop effective communication and engagement strategies.',
    },
  ];

  const navigate = useNavigate();
  const scrollToContact = () => navigate('/contacto');

  // Filter TOP 3 and TOP 5
  const top3Challenges = challenges2026.filter(c => c.priority === 'TOP3');
  const top5Challenges = challenges2026.filter(c => c.priority === 'TOP5');

  return (
    <section
      id="retos"
      data-testid="challenges-section"
      ref={ref}
      style={{
        background: 'linear-gradient(180deg, #000 0%, rgba(3,8,3,0.99) 50%, #000 100%)',
        paddingTop: '7rem',
        paddingBottom: '7rem',
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Bg glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(141,194,31,0.04) 0%, transparent 70%)' }}
      />

      <div className="v-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-4"
        >
          <span className="v-tag green">
            {lang === 'es' ? 'RETOS 2026' : 'CHALLENGES 2026'}
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-[1.1] tracking-tight">
              {lang === 'es' ? (
                <>
                  Los <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>8 retos</em>{' '}
                  que definen nuestro{' '}
                  <span style={{ color: '#8DC21F' }}>futuro</span>
                </>
              ) : (
                <>
                  The <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>8 challenges</em>{' '}
                  shaping our{' '}
                  <span style={{ color: '#8DC21F' }}>future</span>
                </>
              )}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <p className="text-base text-white/55 leading-relaxed mb-6">
              {lang === 'es'
                ? 'Seleccionados por +40 participantes en workshops internos. Priorizados por el Comité de Innovación para H2 2026 y 2027. Estos son los retos con mayor impacto estratégico para el negocio, operaciones y posicionamiento competitivo de Valoriza.'
                : 'Selected by +40 participants in internal workshops. Prioritized by the Innovation Committee for H2 2026 and 2027. These are the challenges with the highest strategic impact on Valoriza\'s business, operations and competitive positioning.'}
            </p>
            <div className="flex flex-wrap gap-4 items-center text-xs text-white/40">
              <div className="flex items-center gap-2">
                <Star size={14} style={{ color: '#ffd700' }} fill="#ffd700" />
                <span>{lang === 'es' ? 'TOP 3: Foco principal' : 'TOP 3: Main focus'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Target size={14} style={{ color: '#3b82f6' }} />
                <span>{lang === 'es' ? 'TOP 5: Radar activo' : 'TOP 5: Active radar'}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* TOP 3 Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mb-8"
        >
          <h3 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-4 flex items-center gap-2">
            <Star size={16} style={{ color: '#ffd700' }} fill="#ffd700" />
            {lang === 'es' ? 'TOP 3 — Foco Principal (Equipo + Recursos Dedicados)' : 'TOP 3 — Main Focus (Team + Dedicated Resources)'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {top3Challenges.map((challenge, i) => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                index={i}
                inView={inView}
                onSubmit={scrollToContact}
                lang={lang}
              />
            ))}
          </div>
        </motion.div>

        {/* TOP 5 Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <h3 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-4 flex items-center gap-2">
            <Target size={16} style={{ color: '#3b82f6' }} />
            {lang === 'es' ? 'TOP 5 — Radar Activo (Seguimiento, Menor Dedicación)' : 'TOP 5 — Active Radar (Monitoring, Less Dedication)'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {top5Challenges.map((challenge, i) => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                index={i + 3}
                inView={inView}
                onSubmit={scrollToContact}
                lang={lang}
              />
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-sm text-white/50 mb-4">
            {lang === 'es' 
              ? '¿Tienes una solución innovadora para alguno de estos retos?' 
              : 'Do you have an innovative solution for any of these challenges?'}
          </p>
          <button
            onClick={scrollToContact}
            className="inline-flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-xl transition-all hover:scale-105"
            style={{ 
              background: '#8DC21F', 
              color: '#000', 
              border: 'none', 
              boxShadow: '0 6px 20px rgba(141,194,31,0.25)', 
              cursor: 'pointer' 
            }}
          >
            {lang === 'es' ? 'Proponer Solución' : 'Submit Solution'}
            <ArrowRight size={16} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
